/**
 * Exact reconciliation of incoming payments against issued invoices, by variable symbol.
 *
 * `run-ai-matching` pairs transactions with receipts; nothing matched an incoming payment
 * to an invoice we issued, even though a Czech bank statement states the variable symbol
 * on every line and that symbol is, by convention, the invoice number being paid. This
 * closes that gap with an exact join rather than a language model.
 *
 * Shared by the browser (GPC import, via src/lib/transactions/matchByVariableSymbol.ts)
 * and the Fio sync (fetch-fio-transactions), so both sources settle invoices by the same
 * rule. No imports at all: Deno and Vite both load it, and vitest covers it
 * (src/lib/transactions/__tests__/matchByVariableSymbol.test.ts).
 */

/** Longest symbol a bank will accept. */
const MAX_DIGITS = 10;

/**
 * Reduce a candidate to a valid variable symbol, or null if it cannot be one.
 *
 * A Czech variable symbol must be numeric and at most 10 digits. Separators are dropped
 * (`2026-01` → `202601`) because that is what a payer keys in, and leading zeroes are
 * dropped because banks treat the symbol as a number and statement formats pad the field.
 *
 * Anything longer than 10 digits returns null rather than being truncated: a silently
 * shortened payment reference is worse than none at all.
 */
export function normalizeVariableSymbol(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "").replace(/^0+/, "");
  if (digits === "" || digits.length > MAX_DIGITS) return null;
  return digits;
}

export interface MatchableTransaction {
  id: string;
  amount: number | null;
  currency: string | null;
  variable_symbol: string | null;
}

export interface MatchableInvoice {
  id: string;
  variable_symbol: string | null;
  invoice_number: string | null;
  total: number | null;
  currency: string | null;
  status: string | null;
}

export type VsMatchConfidence = "exact" | "amount_mismatch";

export interface VsMatch {
  transactionId: string;
  invoiceId: string;
  /** 1.0 for an exact amount agreement, 0.8 when only the symbol agrees. */
  score: number;
  confidence: VsMatchConfidence;
  symbol: string;
  transactionAmount: number;
  invoiceTotal: number | null;
}

export interface VsAmbiguity {
  transactionId: string;
  symbol: string;
  invoiceIds: string[];
}

export interface VsMatchResult {
  matches: VsMatch[];
  /** A symbol shared by several invoices — never auto-matched. */
  ambiguous: VsAmbiguity[];
}

/** Same rule as normalizeVariableSymbol, under the name the matcher has always used. */
export const normalizeSymbol = normalizeVariableSymbol;

/** The symbol a payer would reference for this invoice. */
function invoiceSymbol(invoice: MatchableInvoice): string | null {
  return normalizeSymbol(invoice.variable_symbol) ?? normalizeSymbol(invoice.invoice_number);
}

/**
 * A draft was never sent and a cancelled invoice should not be settled, so neither is a
 * candidate. `sent`, `overdue` and `paid` all are — `paid` included so that re-running
 * over an already-reconciled statement is stable rather than producing a second answer.
 *
 * Exported so the invoice query can filter on the same list in SQL.
 */
export const MATCHABLE_INVOICE_STATUSES = ["sent", "overdue", "paid"] as const;
const MATCHABLE_STATUSES = new Set<string>(MATCHABLE_INVOICE_STATUSES);

const minor = (n: number) => Math.round(n * 100);

export function matchByVariableSymbol(
  transactions: MatchableTransaction[],
  invoices: MatchableInvoice[]
): VsMatchResult {
  // Index candidate invoices by their normalized symbol.
  const bySymbol = new Map<string, MatchableInvoice[]>();
  for (const invoice of invoices) {
    if (!MATCHABLE_STATUSES.has(invoice.status ?? "")) continue;
    const symbol = invoiceSymbol(invoice);
    if (!symbol) continue;
    const bucket = bySymbol.get(symbol);
    if (bucket) bucket.push(invoice);
    else bySymbol.set(symbol, [invoice]);
  }

  const matches: VsMatch[] = [];
  const ambiguous: VsAmbiguity[] = [];

  for (const transaction of transactions) {
    // Only incoming money settles an invoice we issued.
    if (transaction.amount === null || transaction.amount <= 0) continue;

    const symbol = normalizeSymbol(transaction.variable_symbol);
    if (!symbol) continue;

    const candidates = bySymbol.get(symbol);
    if (!candidates || candidates.length === 0) continue;

    if (candidates.length > 1) {
      ambiguous.push({
        transactionId: transaction.id,
        symbol,
        invoiceIds: candidates.map((i) => i.id),
      });
      continue;
    }

    const invoice = candidates[0];

    // A currency disagreement means this is not the same obligation, symbol or not.
    if (
      transaction.currency &&
      invoice.currency &&
      transaction.currency.toUpperCase() !== invoice.currency.toUpperCase()
    ) {
      continue;
    }

    const amountAgrees =
      invoice.total !== null && Math.abs(minor(transaction.amount) - minor(invoice.total)) <= 1;

    matches.push({
      transactionId: transaction.id,
      invoiceId: invoice.id,
      score: amountAgrees ? 1 : 0.8,
      confidence: amountAgrees ? "exact" : "amount_mismatch",
      symbol,
      transactionAmount: transaction.amount,
      invoiceTotal: invoice.total,
    });
  }

  return { matches, ambiguous };
}
